import { isNil } from 'lodash'
import React, { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { expandLeftToolbar, collapseLeftToolbar } from '../../../action/left-toolbar-state-actions'
import { createContent, buildContent } from '../infoWindow'
import { infoWindowOpenAction, infoWindowCloseAction } from '../../../action/infowindow-action'
import { selectedObjectId, selectedObjectIdNull } from '../../../action/map-object-id'
import sharedCluster, { createInvisibleMarker, rebuildCluster } from '../sharedMarkerCluster'
import { trackPolylineOptions } from '../../../lib/trackColor'
import { contentVisibilityKey } from '../../../action/hidden-content-ids-actions'
import { visibleActiveVendors } from '../../../lib/layerVisibility'

const RenderLines = (props) => {
  const [infowindowsState, setInfowindowsState] = useState(null)
  const [previousState, setPreviousState] = useState(null)
  let { infoWindowOpenAction, infoWindow, selectedObjectId, selectedObjectIdNull } = props
  let openInfoWindow = null;
  const linesRefs = useRef({});
  const lineClusterMarkersRef = useRef([]);
  const lineMarkerMapRef = useRef({});

  // Cleanup when lines are deleted from Redux state
  useEffect(() => {
    if (!props.mapReference) return;
    const currentIds = new Set(props.lines.map(l => l.id));
    const removedIds = Object.keys(linesRefs.current).filter(id => !currentIds.has(Number(id)));
    if (removedIds.length === 0) return;
    removedIds.forEach(id => {
      linesRefs.current[id].setMap(null);
      delete linesRefs.current[id];
      const invisibleMarker = lineMarkerMapRef.current[id];
      if (invisibleMarker) {
        invisibleMarker.map = null;
        sharedCluster.lineMarkers = sharedCluster.lineMarkers.filter(m => m !== invisibleMarker);
        delete lineMarkerMapRef.current[id];
      }
    });
    rebuildCluster(props.mapReference);
  }, [props.lines]);

  // My Content per-type show/hide for shapes (lines are part of "shapes"). Remove
  // each user line's shape + cluster proxy and clear mapObj when hidden so the
  // render-body diff re-draws it when shown. Vendor lines are unaffected.
  useEffect(() => {
    if (!props.mapReference) return;
    if (!props.hiddenContentTypes.includes('shapes')) return;
    let changed = false;
    props.lines.forEach(line => {
      const id = line.id;
      if (linesRefs.current[id]) {
        try { linesRefs.current[id].setMap(null) } catch (e) { /* already gone */ }
        delete linesRefs.current[id];
      }
      const invisibleMarker = lineMarkerMapRef.current[id];
      if (invisibleMarker) {
        invisibleMarker.map = null;
        sharedCluster.lineMarkers = sharedCluster.lineMarkers.filter(m => m !== invisibleMarker);
        lineClusterMarkersRef.current = lineClusterMarkersRef.current.filter(m => m !== invisibleMarker);
        delete lineMarkerMapRef.current[id];
        changed = true;
      }
      if (line.mapObj) {
        try { line.mapObj().setMap(null) } catch (e) { /* already gone */ }
        line.mapObj = null;
        changed = true;
      }
    });
    if (changed) rebuildCluster(props.mapReference);
  }, [props.hiddenContentTypes, props.mapReference, props.lines]);

  // My Content per-OBJECT hide (WEB-7047). Remove individually-hidden lines and
  // clear mapObj so the render-body below re-draws them when shown again.
  useEffect(() => {
    if (!props.mapReference) return;
    let changed = false;
    props.lines.forEach(line => {
      if (!props.hiddenContentIds.includes(contentVisibilityKey('line', line.id))) return;
      const id = line.id;
      if (linesRefs.current[id]) {
        try { linesRefs.current[id].setMap(null) } catch (e) { /* already gone */ }
        delete linesRefs.current[id];
      }
      const invisibleMarker = lineMarkerMapRef.current[id];
      if (invisibleMarker) {
        invisibleMarker.map = null;
        sharedCluster.lineMarkers = sharedCluster.lineMarkers.filter(m => m !== invisibleMarker);
        lineClusterMarkersRef.current = lineClusterMarkersRef.current.filter(m => m !== invisibleMarker);
        delete lineMarkerMapRef.current[id];
        changed = true;
      }
      if (line.mapObj) {
        try { line.mapObj().setMap(null) } catch (e) { /* already gone */ }
        line.mapObj = null;
        changed = true;
      }
    });
    if (changed) rebuildCluster(props.mapReference);
  }, [props.hiddenContentIds, props.mapReference, props.lines]);

  useEffect(() => {
    if (previousState) {
      previousState.close()
    }
    setPreviousState(infowindowsState)
  }, [infowindowsState])

  let storeInfoBox = (value) => {
    setInfowindowsState(value);
    infoWindowOpenAction(value)
  }

  const { expandLeftToolbar, lines, mapReference, activeVendors, vendors, collapseLeftToolbar, hiddenContentTypes, hiddenContentIds } = props,
    hideShapes = hiddenContentTypes.includes('shapes'),
    vendorLines = activeVendors.map(id => {
      return vendors[id] && vendors[id]['objects'] && vendors[id]['objects']['lines'] ?
        vendors[id]['objects']['lines'].filter(line => isNil(line.mapObj) && !isNil(line.geoJson)) : []
    }),
    toAdd = (hideShapes ? [] : lines.filter(line => isNil(line.mapObj) && !isNil(line.geoJson) && !hiddenContentIds.includes(contentVisibilityKey('line', line.id)))).concat(vendorLines.flat())
  if (!isNil(mapReference) && toAdd.length > 0) {
    const shapeData = [];
    toAdd.forEach(line => {
      if (!linesRefs.current[line.id]) {  // Only add marker if not already on the map
      } else {
        linesRefs.current[line.id].setMap(null)
        delete linesRefs.current[line.id];
      }
      line['measureType'] = 'line'
      let geoJson
      try { geoJson = JSON.parse(line.geoJson) } catch (e) { return }
      if (!geoJson?.features?.[0]) return
      const lineProps = geoJson.features[0].properties?.line || {}
      const rawColor = lineProps.color ?? geoJson.features[0].properties?.color
      const strokeColor = rawColor
        ? (rawColor.length === 9 ? '#' + rawColor.substring(3, 9) : rawColor)
        : '#0009F9'
      // Honor the stored line style + width (solid/dashed/dots), same options tracks use.
      const lineStyle = lineProps.style === 'dashed' || lineProps.style === 'dots' ? lineProps.style : 'solid'
      const coords = geoJson.features[0].geometry.coordinates.map(coord => new google.maps.LatLng(coord[1], coord[0])),
        mapline = new google.maps.Polyline({
          name: line.name,
          map: null,
          path: coords,
          ...trackPolylineOptions(strokeColor, lineStyle, lineProps.width),
          zIndex: 10,
          options: {
            draggable: false,
            clickable: true,
            cursor: 'pointer'
          }
        })
      line['mapObj'] = function () { return mapline }
      mapline.addListener('click', (event) => {
        selectedObjectId(line.id)
        const latLng = event.latLng;
        const lat = latLng.lat();
        const lng = latLng.lng();
        let infoPosition = { lat: lat, lng: lng }
        let editFunction = (infoWindow) => {
          closeFunction(infoWindow)
          // infoWindowOpenAction(null)
          collapseLeftToolbar()
          expandLeftToolbar({ dialog: 'edit', editable: line })
          selectedObjectIdNull()
        }

        const closeFunction = (infoWindow) => {
          openInfoWindow = infoWindow;
          openInfoWindow.close();
          // infoWindowOpenAction(null)
          setInfowindowsState(null)
          selectedObjectIdNull()
        }

        const updateInfoWindow = (value) => {
          openInfoWindow = value
        }

        const dispatchFunction = (value) => {
          storeInfoBox(value)
        }

        createContent({ shapeDetails: line, mapObj: mapline, infoWindow, type: 'line', infoPosition }, { editFunction, closeFunction, updateInfoWindow }, dispatchFunction);
      })
      linesRefs.current[line.id] = mapline;
      const midCoord = geoJson.features[0].geometry.coordinates[Math.floor(geoJson.features[0].geometry.coordinates.length / 2)];
      shapeData.push({ id: line.id, pos: { lat: midCoord[1], lng: midCoord[0] }, shape: mapline });
    });

    lineClusterMarkersRef.current.forEach(m => { m.map = null; });
    Promise.all(shapeData.map(({ pos }) => createInvisibleMarker(pos))).then(markers => {
      const validMarkers = [];
      markers.forEach((m, i) => {
        if (!m) return; // shape had invalid geoJson coordinates; skip it (WEB-7033)
        m._shape = shapeData[i].shape;
        lineMarkerMapRef.current[shapeData[i].id] = m;
        validMarkers.push(m);
      });
      lineClusterMarkersRef.current = validMarkers;
      sharedCluster.lineMarkers = validMarkers;
      rebuildCluster(mapReference);
    });
  }
  return null
}

const mapStateToProps = state => ({
  // Hidden by the global layers eye toggle along with the layers they came from (WEB-7048).
  activeVendors: visibleActiveVendors(state),
  vendors: state.vendors,
  mapReference: typeof state.mapReference === 'function' ? state.mapReference() : null,
  lines: state.lines,
  disableMapObj: state.disableMapObj,
  infoWindow: state.infoWindow,
  hiddenContentTypes: state.hiddenContentTypes,
  hiddenContentIds: state.hiddenContentIds
})

const mapDispatchToProps = dispatch => ({
  expandLeftToolbar: props => dispatch(expandLeftToolbar(props)),
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
  infoWindowOpenAction: (payload) => dispatch(infoWindowOpenAction(payload)),
  selectedObjectId: (payload) => dispatch(selectedObjectId(payload)),
  selectedObjectIdNull: () => dispatch(selectedObjectIdNull()),
})

export default connect(mapStateToProps, mapDispatchToProps)(RenderLines)